import React, { useState } from "react";
import {
  Row,
  Col,
  Tabs,
  Button,
  Input,
  Table,
  Tag,
  Select,
  Space,
  Typography,
} from "antd";
import {
  SearchOutlined,
  ClockCircleOutlined,
  CheckOutlined,
  ShoppingCartOutlined,
  GiftOutlined,
  DollarOutlined,
  BookOutlined,
  CloseOutlined,
  MinusCircleOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import "./ConsumerApplications.css";

const { Title } = Typography;

interface ApplicationData {
  key: string;
  firstName: string;
  lastName: string;
  aiStatus: "approved" | "declined" | "pending";
  eBikeIncentive: string;
  safetyGearIncentive: string;
  totalIncentive: string;
  dateSubmitted: string;
  email: string;
  phone: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
}

const ConsumerApplications: React.FC = () => {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState("pending");
  const [searchText, setSearchText] = useState("");

  const applicationData: ApplicationData[] = [
    {
      key: "1",
      firstName: "Henry",
      lastName: "Robson",
      aiStatus: "approved",
      eBikeIncentive: "$1,000",
      safetyGearIncentive: "$150",
      totalIncentive: "$1,150",
      dateSubmitted: "03/24/2025",
      email: "sampleemail@email.com",
      phone: "555 123 4321",
      address1: "42 Windmill Ln",
      city: "Portland",
      state: "OR",
      zip: "97201",
    },
    {
      key: "2",
      firstName: "Ameeka",
      lastName: "Travis",
      aiStatus: "declined",
      eBikeIncentive: "$1,000",
      safetyGearIncentive: "$150",
      totalIncentive: "$1,150",
      dateSubmitted: "03/24/2025",
      email: "sampleemail@email.com",
      phone: "555 123 4321",
      address1: "1234 Sunshine Dr",
      city: "Portland",
      state: "OR",
      zip: "97202",
    },
    {
      key: "3",
      firstName: "Ronaldo",
      lastName: "Melendez",
      aiStatus: "approved",
      eBikeIncentive: "$500",
      safetyGearIncentive: "$150",
      totalIncentive: "$650",
      dateSubmitted: "03/24/2025",
      email: "sampleemail@email.com",
      phone: "555 123 4321",
      address1: "542 Aloma",
      city: "Portland",
      state: "OR",
      zip: "97203",
    },
    {
      key: "4",
      firstName: "Carlos",
      lastName: "Tolentino",
      aiStatus: "approved",
      eBikeIncentive: "$1,000",
      safetyGearIncentive: "$150",
      totalIncentive: "$1,150",
      dateSubmitted: "03/25/2025",
      email: "sampleemail@email.com",
      phone: "555 123 4321",
      address1: "2 Aruba",
      address2: "56 C",
      city: "Portland",
      state: "OR",
      zip: "97204",
    },
    {
      key: "5",
      firstName: "Ashleigh",
      lastName: "Lukes",
      aiStatus: "approved",
      eBikeIncentive: "$500",
      safetyGearIncentive: "$150",
      totalIncentive: "$650",
      dateSubmitted: "03/25/2025",
      email: "sampleemail@email.com",
      phone: "555 123 4321",
      address1: "32485 Walter St",
      city: "Portland",
      state: "OR",
      zip: "97205",
    },
    {
      key: "6",
      firstName: "Amber",
      lastName: "Charten",
      aiStatus: "declined",
      eBikeIncentive: "$500",
      safetyGearIncentive: "$150",
      totalIncentive: "$650",
      dateSubmitted: "03/22/2025",
      email: "sampleemail@email.com",
      phone: "555 123 4321",
      address1: "3258 Oak",
      city: "Portland",
      state: "OR",
      zip: "97206",
    },
    {
      key: "7",
      firstName: "Leo",
      lastName: "Wick",
      aiStatus: "pending",
      eBikeIncentive: "$500",
      safetyGearIncentive: "$150",
      totalIncentive: "$650",
      dateSubmitted: "03/22/2025",
      email: "sampleemail@email.com",
      phone: "555 123 4321",
      address1: "2 Ridge Route",
      city: "Portland",
      state: "OR",
      zip: "97207",
    },
    {
      key: "8",
      firstName: "Trinh",
      lastName: "Tran",
      aiStatus: "pending",
      eBikeIncentive: "$1,500",
      safetyGearIncentive: "$150",
      totalIncentive: "$1,650",
      dateSubmitted: "03/22/2025",
      email: "sampleemail@email.com",
      phone: "555 123 4321",
      address1: "99443 Bloomington Ct",
      address2: "Suite 100",
      city: "Portland",
      state: "OR",
      zip: "97208",
    },
    {
      key: "9",
      firstName: "Brandon",
      lastName: "Armstrong",
      aiStatus: "pending",
      eBikeIncentive: "$500",
      safetyGearIncentive: "$150",
      totalIncentive: "$650",
      dateSubmitted: "03/22/2025",
      email: "sampleemail@email.com",
      phone: "555 123 4321",
      address1: "56 Estrella",
      city: "Portland",
      state: "OR",
      zip: "97209",
    },
    {
      key: "10",
      firstName: "Julia",
      lastName: "Webb",
      aiStatus: "pending",
      eBikeIncentive: "$1,500",
      safetyGearIncentive: "$150",
      totalIncentive: "$1,650",
      dateSubmitted: "03/21/2025",
      email: "sampleemail@email.com",
      phone: "555 123 4321",
      address1: "52833 Adelanto",
      city: "Portland",
      state: "OR",
      zip: "97210",
    },
  ];

  const getAIStatusTag = (status: string) => {
    switch (status) {
      case "approved":
        return <Tag className="ai-status-approved">AI Approved</Tag>;
      case "declined":
        return <Tag className="ai-status-declined">AI Declined</Tag>;
      case "pending":
        return <Tag className="ai-status-pending">AI Pending</Tag>;
      default:
        return null;
    }
  };

  const columns: ColumnsType<ApplicationData> = [
    {
      title: "First Name",
      dataIndex: "firstName",
      key: "firstName",
      width: 250,
      fixed: "left",
    },
    {
      title: "Last Name",
      dataIndex: "lastName",
      key: "lastName",
      width: 150,
    },
    {
      title: "Decline Reason",
      key: "declineReason",
      width: 200,
      render: () => (
        <Select
          className="decline-reason-select"
          placeholder="Decline Reason"
          suffixIcon={<div className="dropdown-arrow" />}
          bordered={true}
        />
      ),
    },
    {
      title: "AI Status",
      dataIndex: "aiStatus",
      key: "aiStatus",
      width: 120,
      render: (status) => getAIStatusTag(status),
    },
    {
      title: "E-Bike Incentive",
      dataIndex: "eBikeIncentive",
      key: "eBikeIncentive",
      width: 140,
    },
    {
      title: "Safety Gear Incentive",
      dataIndex: "safetyGearIncentive",
      key: "safetyGearIncentive",
      width: 160,
    },
    {
      title: "Total Incentive Amount",
      dataIndex: "totalIncentive",
      key: "totalIncentive",
      width: 180,
    },
    {
      title: "Date Submitted",
      dataIndex: "dateSubmitted",
      key: "dateSubmitted",
      width: 140,
    },
    {
      title: "Email Address",
      dataIndex: "email",
      key: "email",
      width: 200,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 120,
    },
    {
      title: "Address 1",
      dataIndex: "address1",
      key: "address1",
      width: 160,
    },
    {
      title: "Address 2",
      dataIndex: "address2",
      key: "address2",
      width: 120,
      render: (text) => text || "",
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      width: 100,
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      width: 80,
    },
    {
      title: "Zip",
      dataIndex: "zip",
      key: "zip",
      width: 100,
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedRows,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRows(selectedRowKeys as string[]);
    },
  };

  const tabItems = [
    {
      key: "pending",
      label: (
        <div className="tab-item">
          <ClockCircleOutlined />
          <span>Pending Applications</span>
          <div className="tab-badge">224</div>
        </div>
      ),
    },
    {
      key: "all",
      label: (
        <div className="tab-item">
          <UnorderedListOutlined />
          <span>All Applications</span>
        </div>
      ),
    },
    {
      key: "approved",
      label: (
        <div className="tab-item">
          <CheckOutlined />
          <span>Approved</span>
        </div>
      ),
    },
    {
      key: "sale-in-progress",
      label: (
        <div className="tab-item">
          <ShoppingCartOutlined />
          <span>Sale In Progress</span>
        </div>
      ),
    },
    {
      key: "redeemed",
      label: (
        <div className="tab-item">
          <GiftOutlined />
          <span>Redeemed</span>
        </div>
      ),
    },
    {
      key: "funded",
      label: (
        <div className="tab-item">
          <DollarOutlined />
          <span>Funded</span>
        </div>
      ),
    },
    {
      key: "waitlisted",
      label: (
        <div className="tab-item">
          <BookOutlined />
          <span>Waitlisted</span>
        </div>
      ),
    },
    {
      key: "declined",
      label: (
        <div className="tab-item">
          <CloseOutlined />
          <span>Declined</span>
        </div>
      ),
    },
    {
      key: "expired",
      label: (
        <div className="tab-item">
          <MinusCircleOutlined />
          <span>Expired</span>
        </div>
      ),
    },
  ];

  return (
    <div className="consumer-applications">
      {/* Page Header */}
      <Row align="middle" gutter={8} className="page-header">
        <Col>
          <div className="consumer-applications-icon">
            <img
              src="/images/consumerappln.png"
              alt="Consumer Applications"
              style={{ width: '24px', height: '24px' }}
            />
          </div>
        </Col>
        <Col>
          <Title level={2} className="page-title">
            Consumer Applications
          </Title>
        </Col>
      </Row>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className="application-tabs"
        />
      </div>

      {/* Action Bar */}
      <Row justify="space-between" align="middle" className="action-bar">
        <Col>
          <Space size="middle">
            <Button type="primary" className="approve-btn">
              Approve
            </Button>
            <Button className="decline-btn">Decline</Button>
            <span className="pending-count">224 Pending Applications</span>
          </Space>
        </Col>
        <Col>
          <div className="search-container">
            <Input
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="search-input"
              suffix={<SearchOutlined className="search-icon" />}
            />
          </div>
        </Col>
      </Row>

      {/* Data Table */}
      <div className="table-container">
        <Table
          columns={columns}
          dataSource={applicationData}
          rowSelection={rowSelection}
          scroll={{ x: 2000, y: 560 }}
          pagination={false}
          className="applications-table"
          size="middle"
        />
      </div>
    </div>
  );
};

export default ConsumerApplications;
